import { useCallback, useMemo, useState } from "react";
import { noop } from "lodash";
import { useApolloClient } from "@apollo/client";

import Graph_QL, { GqlHook_Returns, StateDataObject } from "..";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { filterByConnection } from "../filterByConnection";
import { BasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { gqlActions } from "../gqlData_Reducer";

import { TerminalItem } from "./LocationFeedingSlices/terminalsSlice";
import { GET_TERMINALS_INFO, TerminalsResponse } from "./GQLCalls/getTerminalsCall";
import { UPDATE_TERMINALS_INFO, UpdateTerminalConfig, UpdateTerminalResponse } from "./GQLCalls/updateTerminalsCall";
import { UPDATE_TERMINAL_CONFIG_FROM_CALIBRATION, UpdateTerminalConfigFromCalibration, UpdateTerminalFromCalibrationResponse } from "./GQLCalls/updateTerminalConfigFromCalibration.tsx";
import { DO_TERMINAL_CALIBRATION, DoTerminalCalibration, DoTerminalCalibrationResponse } from "./GQLCalls/doTerminalCalibration";
import { SET_TERMINAL_IN_CALIBRATION_MODE, SetTerminalCalibrationResponse, SetTerminalInCalibrationMode } from "./GQLCalls/setTerminalInCalibrationModeCall";
import { DO_EMPTY_FEED, DoEmptyFeedResponse } from "./GQLCalls/doEmptyFeed";

interface maintainTerminalElements {
  updateTerminal: (terminalId: number, config: UpdateTerminalConfig, specific?: Graph_QL.GQLCons) => void;
  updateTerminalConfigFromCalibration: (terminalId: number, gramPerRotation: number, specific?: Graph_QL.GQLCons) => Promise<boolean>;
  doTerminalCalibration: (terminalId: number, triggerAmount: number, pauseTime: number, calibrationAmount: number, specific?: Graph_QL.GQLCons) => void
  doEmptyFeed: (terminalId: number, triggerAmount: number, pauseTime: number, amount: number, specific?: Graph_QL.GQLCons) => Promise<boolean>;
  setTerminalInCalibrationMode: (terminalId: number, state: boolean, specific?: Graph_QL.GQLCons) => Promise<boolean>;
}

export const useTerminals = (connection: Graph_QL.GQLCons): GqlHook_Returns<TerminalItem, BasicQueryInfoInput> & maintainTerminalElements => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();

  const terminalsState = useAppSelector(({graphQlData:{domain}}) => domain.terminals);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(terminalsState, Graph_QL.ToArr(connection)) as Record<string, TerminalItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<TerminalItem>)
  },[connection, terminalsState]);

  const getTerminalsInfo = useCallback(async (input: BasicQueryInfoInput, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    try {
      client.query<TerminalsResponse>({query: GET_TERMINALS_INFO, variables: {...usedConnection}})
        .then((e) => {
          if (e && e.data) {
            dispatch({type: String(gqlActions.domain.terminals.maintained), payload: {con: connection?.connection, data: e.data.terminal.info}});
          }
        })
    } catch (error) {
        console.log(error);
      }
    },[client, connection.connection, dispatch])

    const updateTerminal = useCallback(async (terminalId: number, config: UpdateTerminalConfig, specific?: Graph_QL.GQLCons) => {
      const usedConnection = specific ? specific.connection : connection.connection 
      try {
        return client.mutate<UpdateTerminalResponse>({mutation: UPDATE_TERMINALS_INFO, variables: {...usedConnection, terminalid: terminalId, config}})
        .then((e) => {
          const updatedTerminal = e.data?.terminal.updateConfig;
          if (updatedTerminal && updatedTerminal.success)
            getTerminalsInfo({fetchAmount: 50})
        })
      } catch (error) {console.log(error)}
    },[client, connection.connection, getTerminalsInfo])

    const updateTerminalConfigFromCalibration = useCallback(async (terminalId: number, gramPerRotation: number, specific?: Graph_QL.GQLCons): Promise<boolean> => {
      const usedConnection = specific ? specific.connection : connection.connection 
      try {
        return client.mutate<UpdateTerminalFromCalibrationResponse>({mutation: UPDATE_TERMINAL_CONFIG_FROM_CALIBRATION, variables: {...usedConnection, terminalid: terminalId, gramPerRotation}})
        .then((e) => {
          const updatedTerminalFromCalibration = e.data?.terminal.updateConfigFromCalibration;
          if (updatedTerminalFromCalibration && updatedTerminalFromCalibration.success) {
            getTerminalsInfo({fetchAmount: 50});
            return true;
          }
          return false;
        }).catch(() => false);
      } catch (error) {
        console.log(error);
        return false;
      }
    },[client, connection.connection, getTerminalsInfo])

    const doTerminalCalibration = useCallback(async (terminalId: number, triggerAmount: number, pauseTime: number, calibrationAmount: number, specific?: Graph_QL.GQLCons) => {
      const usedConnection = specific ? specific.connection : connection.connection 
      try {
        return client.mutate<DoTerminalCalibrationResponse>({mutation: DO_TERMINAL_CALIBRATION, variables: {...usedConnection, terminalid: terminalId, triggerAmount, pauseTime, calibrationAmount}})
        .then((e) => {
          const doneTerminalCalibration = e.data?.terminal.doCalibration;
          if (doneTerminalCalibration && doneTerminalCalibration.success)
            getTerminalsInfo({fetchAmount: 50})
        })
      } catch (error) {console.log(error)}
    }, [client, connection.connection, getTerminalsInfo])
  
    const doEmptyFeed = useCallback(async (terminalId: number, triggerAmount: number, pauseTime: number, amount: number, specific?: Graph_QL.GQLCons) => {
      const usedConnection = specific ? specific.connection : connection.connection 
      try {
        return client.mutate<DoEmptyFeedResponse>({mutation: DO_EMPTY_FEED, variables: {...usedConnection, terminalid: terminalId, triggerAmount, pauseTime, amount}})
        .then((e) => {
          const doneEmptyFeed = e.data?.terminal.doEmptyFeed;
          if (doneEmptyFeed) {
            return doneEmptyFeed.success? true:false;
          }
          return false;
        }).catch((error) => {return false})
      } catch (error) {
        console.log(error)
        return false
      }
    },[client, connection.connection])

    const setTerminalInCalibrationMode = useCallback(async (terminalId: number, state: boolean, specific?: Graph_QL.GQLCons) => {
      const usedConnection = specific ? specific.connection : connection.connection 
      try {
        return client.mutate<SetTerminalCalibrationResponse>({mutation: SET_TERMINAL_IN_CALIBRATION_MODE, variables: {...usedConnection, terminalid: terminalId, state}})
        .then((e) => {
          const terminalCalibration = e.data?.terminal.setInCalibrationMode;
          if (terminalCalibration) {
            return terminalCalibration.success;
          }
          return false;
        }).catch((error) => {return false})
      } catch (error) {console.log(error); return false}
    },[client, connection.connection])

  return {call: getTerminalsInfo, updateTerminal, updateTerminalConfigFromCalibration, doTerminalCalibration, doEmptyFeed, setTerminalInCalibrationMode, fetchMore: noop, deleteEntries: noop, data: fullState, maintainEntries: noop}
} 

